import { useAppDispatch, useAppSelector } from '@/common/reducers/hooks/store.hook';
import { useLazyThumbnail } from '@common/hooks/use-lazy-thumbnail';
import { setLinkPreview, setSendLinkPreview } from '@common/reducers/ScheduledMessageEditorReducer';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ChangeEvent, memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';



type LinkPreviewBoxProps = {
  isPreview: boolean;
};

type LinkPreviewData = {
  title?: string;
  description?: string;
  url?: string;
};

type LinkPreviewFieldType = 'title' | 'description' | 'url';

type LinkPreviewFieldInputProps = {
  fieldType: LinkPreviewFieldType;
  style: React.CSSProperties;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  isPreview: boolean;
};

const INPUT_STYLES = {
  base: {
    width: '100%',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--tasks-cell-border)',
    borderRadius: '6px',
    outline: 'none',
    resize: 'none' as const,
    padding: '8px 12px',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--tasks-text-secondary)',
    transition: 'all 0.2s ease',
    cursor: 'text',
    position: 'relative' as const,
  },
  hover: {
    borderColor: 'var(--color-accent)',
    boxShadow: '0 0 0 3px var(--color-surface-accent)',
  },
  focus: {
    backgroundColor: 'var(--color-surface)',
    borderColor: 'var(--color-accent)',
    boxShadow: '0 0 0 3px var(--color-surface-accent)',
  },
  preview: {
    cursor: 'default',
    backgroundColor: 'transparent',
    border: 'none',
    padding: '8px 0',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '1.4',
    minHeight: '20px',
  },
  description: {
    fontSize: '14px',
    lineHeight: '1.5',
    minHeight: '36px',
  },
  url: {
    fontSize: '13px',
    fontFamily: 'monospace',
    color: 'var(--color-text-muted)',
    minHeight: '18px',
  }
} as const;

const LAYOUT_STYLES = {
  container: {
    borderRadius: '8px',
    overflow: 'hidden',
    alignItems: 'stretch',
  },
  imageContainer: {
    minWidth: '80px',
    maxWidth: '150px',
    width: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: '8px',
    backgroundColor: 'var(--color-surface-muted)',
    backgroundImage: 'linear-gradient(45deg, rgba(0, 0, 0, 0.02) 25%, transparent 25%), linear-gradient(-45deg, rgba(0, 0, 0, 0.02) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(0, 0, 0, 0.02) 75%), linear-gradient(-45deg, transparent 75%, rgba(0, 0, 0, 0.02) 75%)',
    backgroundSize: '8px 8px',
    backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
    marginTop: '5px',
    marginBottom: '5px',
  },
  image: {
    height: '100%',
    width: '100%',
    objectFit: 'contain' as const,
    maxWidth: '100%',
    maxHeight: '100%',
    margin: 0,
  },
  contentContainer: {
    flex: 1,
    padding: '4px 13px',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'space-between',
    minHeight: '96px',
  }
} as const;

/**
 * Skeleton loading state for link preview input fields
 */
const LinkPreviewFieldSkeleton = memo(function LinkPreviewFieldSkeleton({
  fieldType,
}: {
  fieldType: LinkPreviewFieldType;
}) {
  const skeletonConfig = useMemo(() => {
    switch (fieldType) {
      case 'title':
        return {
          height: 24,
          width: '80%',
          label: 'Link preview title',
        };
      case 'description':
        return {
          height: 40,
          width: '95%',
          label: 'Link preview description',
        };
      case 'url':
        return {
          height: 18,
          width: '60%',
          label: 'Link preview URL',
        };
      default:
        return {
          height: 20,
          width: '70%',
          label: 'Link preview field',
        };
    }
  }, [fieldType]);

  return (
    <Box sx={{ width: '100%' }}>
      <Skeleton
        variant="text"
        width={80}
        height={14}
        sx={{
          marginBottom: '4px',
          backgroundColor: 'var(--color-surface-muted)',
        }}
      />
      <Skeleton
        variant="rounded"
        width={skeletonConfig.width}
        height={skeletonConfig.height}
        sx={{
          backgroundColor: 'var(--color-surface-muted)',
          borderRadius: '6px',
        }}
      />
    </Box>
  );
});

/**
 * Input field specifically designed for editing link preview fields (title, description, URL)
 * Provides visual feedback and appropriate styling for each field type
 */
const LinkPreviewFieldInput = memo(function LinkPreviewFieldInput({
  fieldType,
  style,
  value,
  onChange,
  isPreview,
}: LinkPreviewFieldInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const fieldConfig = useMemo(() => {
    switch (fieldType) {
      case 'title':
        return {
          placeholder: isPreview ? '' : 'Enter title...',
          multiline: false,
          ariaLabel: 'Link preview title',
          label: 'Link preview title',
        };
      case 'description':
        return {
          placeholder: isPreview ? '' : 'Enter description...',
          multiline: true,
          ariaLabel: 'Link preview description',
          label: 'Link preview description',
        };
      case 'url':
        return {
          placeholder: isPreview ? '' : 'Enter URL...',
          multiline: false,
          ariaLabel: 'Link preview URL',
          label: 'Link preview URL',
        };
      default:
        return {
          placeholder: '',
          multiline: false,
          ariaLabel: 'Link preview field',
          label: 'Link preview field',
        };
    }
  }, [fieldType, isPreview]);

  const inputStyle = useMemo(() => ({
    ...INPUT_STYLES.base,
    ...style,
    ...(isPreview ? INPUT_STYLES.preview : {}),
    ...(!isPreview && isHovered ? INPUT_STYLES.hover : {}),
    ...(!isPreview && isFocused ? INPUT_STYLES.focus : {}),
  }), [style, isFocused, isHovered, isPreview]);

  const Component = fieldConfig.multiline ? 'textarea' : 'input';

  return (
    <Box sx={{ width: '100%' }}>
      {!isPreview && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            marginBottom: '4px',
            fontSize: '11px',
            fontWeight: 400,
            color: 'var(--color-text-muted)',
            textTransform: 'none',
            letterSpacing: 'normal',
          }}
        >
          {fieldConfig.label}
        </Typography>
      )}

      <Box
        sx={{
          position: 'relative',
          cursor: isPreview ? 'default' : 'text',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Component
          style={inputStyle}
          placeholder={fieldConfig.placeholder}
          aria-label={fieldConfig.ariaLabel}
          disabled={isPreview}
          onChange={onChange}
          value={value}
          rows={fieldConfig.multiline ? 2 : undefined}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          dir="auto"
        />
      </Box>
    </Box>
  );
});

/**
 * Container component for all link preview input fields
 * Handles the layout and state management for title and description editing
 * 
 * This follows the exact same proven pattern used in message-editor-message-box.tsx
 */
const LinkPreviewInputFields = memo(function LinkPreviewInputFields({
  isPreview,
}: {
  isPreview: boolean;
}) {
  const dispatch = useAppDispatch();

  // Read Redux state for initialization and external updates
  const reduxLinkPreview = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.linkPreview);
  const isLoading = useAppSelector(state => state.AddScheduledMessageButtonReducer.linkPreviewLoading);

  // Local state for immediate UI updates - initialized from Redux (already decoded)
  const [localLinkPreview, setLocalLinkPreview] = useState<LinkPreviewData>(() => reduxLinkPreview || {});

  // Use deferred value for Redux sync - React 18 best practice
  const deferredLinkPreview = useDeferredValue(localLinkPreview);

  // Ref to track when we're syncing from Redux to avoid circular updates
  const isSyncingFromRedux = useRef(false);

  const handleLinkPreviewFieldChange = useCallback((field: keyof LinkPreviewData) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Update local state immediately for instant UI feedback
    setLocalLinkPreview(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  }, []);

  // Sync local state with Redux when Redux changes externally
  // This follows the exact same pattern as message-editor-message-box.tsx
  useEffect(() => {
    // Redux data is already decoded, so use it directly
    const reduxData = reduxLinkPreview || {};
    
    // Update local state when Redux changes externally (not during our own updates)
    if (!isSyncingFromRedux.current) {
      setLocalLinkPreview(currentLocal => {
        // Compare individual fields instead of expensive JSON.stringify
        const hasExternalChanges = 
          reduxData.title !== currentLocal.title ||
          reduxData.description !== currentLocal.description ||
          reduxData.url !== currentLocal.url;

        if (hasExternalChanges) {
          isSyncingFromRedux.current = true;
          
          // Reset flag after state update - same timing as message box
          setTimeout(() => {
            isSyncingFromRedux.current = false;
          }, 0);
          
          return reduxData;
        }
        
        return currentLocal;
      });
    }
  }, [reduxLinkPreview]); // Only depend on reduxLinkPreview

  // Sync deferred local changes to Redux (only when user types, not from Redux sync)
  useEffect(() => {
    // Redux data is already decoded, so use it directly
    const reduxData = reduxLinkPreview || {};
    
    // Compare individual fields instead of expensive JSON.stringify
    const hasLocalChanges = 
      deferredLinkPreview.title !== reduxData.title ||
      deferredLinkPreview.description !== reduxData.description ||
      deferredLinkPreview.url !== reduxData.url;

    if (hasLocalChanges && !isSyncingFromRedux.current) {
      dispatch(setLinkPreview(deferredLinkPreview));
    }
  }, [deferredLinkPreview, dispatch]); // Only depend on deferredLinkPreview and dispatch


  const fieldStyles = useMemo(() => ({
    title: INPUT_STYLES.title,
    description: INPUT_STYLES.description,
    url: INPUT_STYLES.url
  }), []);

  return (
    <Box sx={{
      ...LAYOUT_STYLES.contentContainer,
      gap: isPreview ? '6px' : '12px',
    }}>
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: isPreview ? '6px' : '12px'
      }}>
        {isLoading &&
          <>
            <LinkPreviewFieldSkeleton
              fieldType="title"
            />
            <LinkPreviewFieldSkeleton
              fieldType="description"
            />
          </>
        }
        {!isLoading &&
          <>
            <LinkPreviewFieldInput
              fieldType="title"
              style={fieldStyles.title}
              onChange={handleLinkPreviewFieldChange('title')}
              value={localLinkPreview?.title ?? ''}
              isPreview={isPreview}
            />

            <LinkPreviewFieldInput
              fieldType="description"
              style={fieldStyles.description}
              onChange={handleLinkPreviewFieldChange('description')}
              value={localLinkPreview?.description ?? ''}
              isPreview={isPreview}
            />
          </>
        }
      </Box>

      {/* URL field temporarily disabled - uncomment when needed
      <LinkPreviewFieldInput
        fieldType="url"
        style={fieldStyles.url}
        onChange={handleLinkPreviewFieldChange('url')}
        value={linkPreview.url ?? ''}
        isPreview={isPreview}
      /> */}
    </Box>
  );
});

/** Derive a display domain from the preview's URL / matched text / site name. */
function previewDomain(lp: any): string {
  const raw = lp?.url || lp?.matchedText || lp?.link || '';
  if (raw) {
    try { return new URL(raw.startsWith('http') ? raw : `https://${raw}`).hostname.replace(/^www\./, ''); } catch { /* fall through */ }
  }
  return lp?.siteName || '';
}

/**
 * FE#384 — the compact link-preview STRIP (replaces the old two tall fields that
 * cost ~200px of composer height). Shows a thumbnail + title + "<domain> · link
 * preview on", an Edit toggle that reveals the title/description editor inline,
 * and an × that removes the preview (the strip owns Cancel Link Preview, so the
 * "Update Link Preview" button is hidden while this is shown). The wired
 * title/description ↔ Redux sync stays in LinkPreviewInputFields.
 */
const LinkPreviewBox = memo(function LinkPreviewBox({
  isPreview,
}: LinkPreviewBoxProps) {
  const dispatch = useAppDispatch();
  const linkPreview = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.linkPreview);
  const isLoading = useAppSelector(state => state.AddScheduledMessageButtonReducer.linkPreviewLoading);
  const [editing, setEditing] = useState(false);
  const { t } = useTranslation(namespace);

  const title = linkPreview?.title || '';
  const draftId = useAppSelector((state: any) => state.AddScheduledMessageButtonReducer.messageDraft?._id) as string | undefined;
  const inlineThumb = linkPreview?.thumbnail || linkPreview?.thumbnailHQ || '';
  const thumb = useLazyThumbnail(draftId, { linkPreview }, inlineThumb) || '';
  const domain = previewDomain(linkPreview);
  const initial = (title || domain || 'L').trim().charAt(0).toUpperCase();

  const removePreview = useCallback(() => {
    setEditing(false);
    dispatch(setSendLinkPreview(false));
    dispatch(setLinkPreview({}));
  }, [dispatch]);

  const stripBg = 'var(--link-strip-bg, #F7FAFD)';

  return (
    <Stack direction="column" sx={{ flex: 'none' }}>
      {/* Strip */}
      <Stack direction="row" alignItems="center" sx={{
        gap: '10px',
        padding: '7px 8px 7px 10px',
        background: stripBg,
        borderBottom: '1px solid var(--tasks-cell-border)',
      }}>
        {/* Thumbnail (og:image) or initial fallback */}
        <Box sx={{
          width: 34, height: 34, flexShrink: 0, borderRadius: '7px', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#1C2431', color: '#fff', fontSize: '15px', fontWeight: 700,
        }}>
          {thumb
            ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : (isLoading ? <Skeleton variant="rectangular" width={34} height={34} /> : initial)}
        </Box>

        {/* Title + domain */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {isLoading && !title ? <Skeleton width={140} /> : (title || domain || 'Link preview')}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: 'var(--color-text-muted, #8A95A6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {domain ? `${domain} · link preview on` : 'link preview on'}
          </Typography>
        </Box>

        {!isPreview && (
          <>
            <Box
              role="button"
              onClick={() => setEditing(v => !v)}
              sx={{
                height: 28, display: 'flex', alignItems: 'center', padding: '0 11px',
                borderRadius: '999px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', userSelect: 'none',
                whiteSpace: 'nowrap', flexShrink: 0,
                border: editing ? '1px solid var(--color-accent)' : '1px solid var(--tasks-cell-border)',
                background: editing ? 'var(--tasks-menu-hover-bg)' : 'var(--color-surface)',
                color: editing ? 'var(--color-accent)' : 'var(--color-text)',
              }}
            >
              {editing ? t('linkPreview.close', 'Close') : t('linkPreview.edit', 'Edit')}
            </Box>
            <Box
              role="button"
              aria-label={t('linkPreview.cancel', 'Cancel link preview')}
              onClick={removePreview}
              sx={{
                width: 28, height: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '8px', cursor: 'pointer', color: 'var(--color-text-muted, #7A8698)',
                '&:hover': { background: 'var(--tasks-menu-hover-bg)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </Box>
          </>
        )}
      </Stack>

      {/* Inline editor (revealed by Edit) — wired title/description ↔ Redux */}
      {!isPreview && editing && (
        <Box sx={{ borderBottom: '1px solid var(--tasks-cell-border)', overflowY: 'auto' }}>
          <LinkPreviewInputFields isPreview={false} />
          <Stack direction="row" spacing={1} sx={{ px: '10px', pb: '12px' }}>
            <Box
              role="button"
              onClick={() => setEditing(false)}
              sx={{
                height: 30, display: 'flex', alignItems: 'center', padding: '0 14px', borderRadius: '999px',
                fontSize: '13px', fontWeight: 700, cursor: 'pointer', userSelect: 'none',
                background: 'var(--color-accent)', color: 'var(--color-accent-contrast, #fff)',
              }}
            >
              {t('linkPreview.done', 'Done')}
            </Box>
            <Box
              role="button"
              onClick={removePreview}
              sx={{
                height: 30, display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: '999px',
                fontSize: '13px', fontWeight: 500, cursor: 'pointer', userSelect: 'none',
                color: 'var(--color-text)', '&:hover': { background: 'var(--tasks-menu-hover-bg)' },
              }}
            >
              {t('linkPreview.remove', 'Remove preview')}
            </Box>
          </Stack>
        </Box>
      )}
    </Stack>
  );
});

export { LinkPreviewBox };

